import {
    createApp } from 'vue';
import { http } from '@pendo/http';
import InactivityModal from '@/common/inactivity/inactivity-modal.vue';
import { EventEmitter } from 'events';
import { logout as logoutFn,
} from '@/stores/api/auth.api';

export let sessionTracker;

/*
 * vueAppElement is never inserted into the document. Normally, rendering a Vue
 * app into an off-document element won't render anything, but in this case,
 * pendo-modal teleports its content, so the teleported content will render in
 * the teleport target.
 */
let vueAppElement;
let vueApp;
let clickEvent = {};
let pageLoadEvent = {};

const DEFAULT_INTERVAL = 60000;
const LAST_ACTIVITY_URL = '/api/s/_SID_/user/lastactivity';

class SessionTracker extends EventEmitter {
    constructor ({ checkInInterval = DEFAULT_INTERVAL, inactivityThreshold } = {}) {
        super();
        if (!inactivityThreshold || inactivityThreshold <= checkInInterval) {
            return;
        }

        this.inactivityThreshold = inactivityThreshold;
        this.maxThreshold = inactivityThreshold - checkInInterval * 2;
        this.updateLastActivityTimestamp();
        this.sendKeepalive();
        this.setup(checkInInterval);
    }

    setup (interval) {
        // Add activity trackers
        this.trackClicks();
        this.trackPageLoads();

        this.timerId = setInterval(this.checkActivity.bind(this), interval);
    }

    dispose () {
        clearInterval(this.timerId);
        document.removeEventListener('click', clickEvent);
        window.removeEventListener('popstate', pageLoadEvent);
        window.removeEventListener('hashchange', pageLoadEvent);
        vueApp?.unmount();
        vueAppElement = null;
    }

    get isActivityWithinThreshold () {
        return Date.now() - this.localLastActivityTimestamp < this.maxThreshold;
    }

    async checkActivity () {
        if (!this.isActivityWithinThreshold) {
            await this.checkGlobalLastActivity();
            if (!this.isActivityWithinThreshold) {
                this.cancelClickTracking();
                this.emit('inactive');

                return;
            }
        }

        this.emit('active');

        if (this.lastTimestampSent !== this.localLastActivityTimestamp) {
            this.sendKeepalive();
        }
    }

    async sendKeepalive () {
        await http
            .put(LAST_ACTIVITY_URL, {
                lastActivity: this.localLastActivityTimestamp,
            })
            .then((response) => {
                const globalLastActivityTimestamp = response.data.lastActivity;
                if (this.localLastActivityTimestamp < globalLastActivityTimestamp) {
                    this.localLastActivityTimestamp = globalLastActivityTimestamp;
                }
            })
            .catch((err) => {
                if (err.response.status === 401) {
                    this.logout();
                }
            });

        this.lastTimestampSent = this.localLastActivityTimestamp;
    }

    async checkGlobalLastActivity () {
        await http
            .get(LAST_ACTIVITY_URL)
            .then((response) => {
                const globalLastActivityTimestamp = response.data.lastActivity;
                if (this.localLastActivityTimestamp < globalLastActivityTimestamp) {
                    this.localLastActivityTimestamp = globalLastActivityTimestamp;
                }
            })
            .catch((err) => {
                if (err.response.status === 401) {
                    this.logout();
                }
            });
    }

    updateLastActivityTimestamp () {
        this.localLastActivityTimestamp = Date.now();
    }

    trackClicks () {
        try {
            clickEvent = this.updateLastActivityTimestamp.bind(this);
            document.addEventListener('click', clickEvent);
        } catch {
            // do nothing
        }
    }

    cancelClickTracking () {
        document.removeEventListener('click', clickEvent);
    }

    trackPageLoads () {
        try {
            // handles forward and back buttons
            pageLoadEvent = this.updateLastActivityTimestamp.bind(this);
            window.addEventListener('popstate', pageLoadEvent);
            window.addEventListener('hashchange', pageLoadEvent);
        } catch {
            // do nothing
        }
    }

    continueSession () {
        this.trackClicks();
        this.updateLastActivityTimestamp();
        this.sendKeepalive();
    }

    async logout () {
        await logoutFn();
        this.dispose();
        window.location.reload(true);
    }
}

export function initializeSessionTracker (params) {
    if (sessionTracker) {
        sessionTracker.dispose();
    }

    sessionTracker = new SessionTracker(params);

    if (params.inactivityThreshold) {
        vueAppElement = document.createElement('div');
        vueApp = createApp(InactivityModal, { sessionTracker });
        vueApp.mount(vueAppElement);
    }

    return sessionTracker;
}
